import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, { Controls, Background, applyNodeChanges, useReactFlow, Node, NodeChange } from 'reactflow';
import { Form, Button, Spinner } from 'react-bootstrap';
import {
  fetchDashboardById,
  createDashboard,
  updateDashboard,
  clearCurrentDashboard,
  DashboardItem,
} from '../../../../../redux/slices/monitoringDashboardsSlice';
import { fetchCustomerNodeDefinitionById } from '../../../../../redux/slices/customer_node_definitionsSlice';
import { getZones } from '../../../../../redux/slices/lists/zonesSlice';
import { FolderSelect } from 'components/forms/FolderSelect';
import FlowCardNode from './components/FlowCardNode';
import FlowLibraryPanel from './components/FlowLibraryPanel';
import './MonitoringDashboard.scss';
import 'reactflow/dist/style.css';

interface EditorNode extends Node {
  node_definition_id: number;
}

const MonitoringDashboardEditor: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dashboardId } = useParams<{ dashboardId?: string }>();
  const [searchParams] = useSearchParams();
  const reactFlowInstance = useReactFlow();

  const { currentDashboard } = useAppSelector((state) => state.monitoringDashboards);
  const { zones } = useAppSelector((state) => state.listOfZones);

  const [dashboardName, setDashboardName] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [folderId, setFolderId] = useState<number | null>(null);
  const [nodes, setNodes] = useState<EditorNode[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Ref to avoid stale closure in onDrop
  const nodesRef = useRef<EditorNode[]>(nodes);
  nodesRef.current = nodes;

  const isEditMode = !!dashboardId;

  const nodeTypes = useMemo(() => ({ flowCard: FlowCardNode }), []);

  // Load zones once on mount
  useEffect(() => {
    dispatch(getZones() as any);
  }, [dispatch]);

  // Load existing dashboard in edit mode
  useEffect(() => {
    if (dashboardId) {
      dispatch(fetchDashboardById(Number(dashboardId)) as any);
    }
  }, [dispatch, dashboardId]);

  // Create pre-fill from the Workspace: ?zoneName & ?folderId seed the new
  // dashboard's zone / folder.
  useEffect(() => {
    if (isEditMode) return;
    const zoneNameParam = searchParams.get('zoneName');
    const folderIdParam = searchParams.get('folderId');
    if (zoneNameParam) setZoneName(zoneNameParam);
    if (folderIdParam) setFolderId(Number(folderIdParam));
  }, [isEditMode, searchParams]);

  // Populate state when dashboard data loads
  useEffect(() => {
    if (currentDashboard && isEditMode) {
      setDashboardName(currentDashboard.name || '');
      setZoneName(currentDashboard.zone_name || '');
      setFolderId(currentDashboard.folder_id ?? null);

      if (currentDashboard.items) {
        const mappedNodes: EditorNode[] = currentDashboard.items.map((item: DashboardItem) => ({
          id: `nd-${item.node_definition_id}`,
          type: 'flowCard',
          position: { x: item.position_x, y: item.position_y },
          data: {
            label: item.node_definition_name,
            mode: 'editor',
            childNodes: (item.child_nodes || []).map((cn) => ({
              flow_node_id: cn.flow_node_id,
              name: cn.name || cn.label,
              type: cn.type,
            })),
          },
          node_definition_id: item.node_definition_id,
        }));
        setNodes(mappedNodes);
      }
    }
  }, [currentDashboard, isEditMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCurrentDashboard());
    };
  }, [dispatch]);

  // Handle node position changes (dragging)
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds) as EditorNode[]);
  }, []);

  // Handle drag start from library panel
  const onDragStart = useCallback((event: React.DragEvent, node: any) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
  }, []);

  // Handle drag over the canvas
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop onto the canvas
  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();

      const rawData = event.dataTransfer.getData('application/reactflow');
      if (!rawData) return;

      let droppedNode: any;
      try {
        droppedNode = JSON.parse(rawData);
      } catch {
        return;
      }

      const { node_definition_id, name } = droppedNode;
      if (!node_definition_id) return;

      // Prevent duplicates using ref (avoids stale closure)
      const alreadyExists = nodesRef.current.some((n) => n.node_definition_id === node_definition_id);
      if (alreadyExists) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Fetch child nodes for the dropped flow definition
      let childNodes: { flow_node_id: string; name: string; type: string }[] = [];
      try {
        const response: any = await dispatch(fetchCustomerNodeDefinitionById(node_definition_id) as any);
        if (response?.payload?.child_nodes) {
          childNodes = response.payload.child_nodes.map((cn: any) => ({
            flow_node_id: cn.id || cn.flow_node_id,
            name: cn.name || cn.label,
            type: cn.type,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch child nodes:', err);
      }

      const newNode: EditorNode = {
        id: `nd-${node_definition_id}`,
        type: 'flowCard',
        position,
        data: {
          label: name,
          mode: 'editor',
          childNodes,
        },
        node_definition_id,
      };

      setNodes((prev) => {
        // Double-check inside updater to be safe
        if (prev.some((n) => n.node_definition_id === node_definition_id)) {
          return prev;
        }
        return [...prev, newNode];
      });
    },
    [dispatch, reactFlowInstance]
  );

  // Handle node deletion via keyboard
  const onNodesDelete = useCallback((deletedNodes: Node[]) => {
    const deletedIds = new Set(deletedNodes.map((n) => n.id));
    setNodes((prev) => prev.filter((n) => !deletedIds.has(n.id)));
  }, []);

  // Save handler — uses nodesRef to guarantee latest nodes
  const handleSave = useCallback(async () => {
    if (!dashboardName.trim()) return;

    setIsSaving(true);

    try {
      const currentNodes = nodesRef.current;
      const items = currentNodes.map((n) => ({
        node_definition_id: n.node_definition_id,
        position_x: n.position.x,
        position_y: n.position.y,
        width: 300,
        height: 200,
      }));

      if (!isEditMode) {
        // Create new dashboard with items in one call
        const result: any = await dispatch(
          createDashboard({
            name: dashboardName.trim(),
            zone_name: zoneName || undefined,
            folder_id: folderId,
            items,
          }) as any
        );

        const newId = result?.payload?.dashboard?.dashboard_id;
        if (newId) {
          navigate(`/monitoring/dashboards/${newId}/edit`, { replace: true });
        }
      } else {
        // Update existing dashboard
        await dispatch(
          updateDashboard({
            dashboard_id: Number(dashboardId),
            name: dashboardName.trim(),
            zone_name: zoneName || undefined,
            folder_id: folderId,
            items,
          }) as any
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [dashboardName, zoneName, folderId, isEditMode, dashboardId, dispatch, navigate]);

  return (
    <div>
      <div className="d-flex align-items-center gap-3 p-3 border-bottom">
        <Form.Control placeholder="Dashboard Name" value={dashboardName} onChange={(e) => setDashboardName(e.target.value)} style={{ maxWidth: 300 }} />
        <Form.Select
          value={zoneName}
          onChange={(e) => {
            // Folder is zone-scoped — clear it when the zone changes.
            setZoneName(e.target.value);
            setFolderId(null);
          }}
          style={{ maxWidth: 200 }}
        >
          <option value="">Select Zone</option>
          {(zones || []).map((z: any) => (
            <option key={z.name} value={z.name}>
              {z.name}
            </option>
          ))}
        </Form.Select>
        <div style={{ maxWidth: 250, minWidth: 180 }}>
          <FolderSelect value={folderId} onChange={setFolderId} zoneName={zoneName} noneLabel="Select Folder" />
        </div>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !dashboardName.trim()}>
          {isSaving ? <Spinner animation="border" size="sm" /> : 'Save'}
        </Button>
        <Button variant="secondary" onClick={() => navigate('/monitoring/dashboards')}>
          Back
        </Button>
      </div>
      <div className="monitoring-editor-container">
        <div className="monitoring-editor-library">
          <FlowLibraryPanel onDragStart={onDragStart} />
        </div>
        <div className="monitoring-editor-canvas" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            proOptions={{ hideAttribution: true }}
            fitView
          >
            <Background />
            <Controls position="top-left" />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboardEditor;
